import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCart } from "@/context/CartContext";
import {
  Search, ShoppingCart, ShoppingBag, MapPin, IndianRupee, Store, BadgeCheck, Star, TrendingUp,
} from "lucide-react";

function priceLabel(p) {
  if (p.price_range) return p.price_range;
  if (typeof p.price_value === "number") return `₹${p.price_value.toLocaleString("en-IN")}`;
  return null;
}

function ProductCard({ p, onAdd }) {
  const price = priceLabel(p);
  const vendor = p.vendor || {};
  const detailPath = `/items/${encodeURIComponent(p.product_key)}`;
  return (
    <Card className="flex flex-col overflow-hidden border-border/60 hover:shadow-md transition-shadow" data-testid={`search-product-card-${p.product_key}`}>
      {/* Everything above the CTA row is a link that opens the detail page in a new tab */}
      <Link
        to={detailPath}
        target="_blank"
        rel="noopener noreferrer"
        className="flex flex-col flex-1"
        data-testid={`search-product-link-${p.product_key}`}
      >
        <div className="aspect-square w-full bg-muted overflow-hidden shrink-0">
          {p.image ? (
            <img src={p.image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
          ) : vendor.logo ? (
            <img src={vendor.logo} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <ShoppingBag className="h-8 w-8" />
            </div>
          )}
        </div>
        <div className="p-3 flex flex-col gap-1.5 flex-1">
          <div className="font-semibold text-sm leading-tight line-clamp-2 hover:text-primary transition-colors" data-testid={`search-product-name-${p.product_key}`}>
            {p.name}
          </div>

          {/* Amazon-style: vendor name lives in the DESCRIPTION section */}
          <div className="text-[11px] text-muted-foreground line-clamp-2 min-h-[28px]" data-testid={`search-product-desc-${p.product_key}`}>
            {p.description}
          </div>
          <div className="text-[11px] inline-flex items-center gap-1 text-muted-foreground truncate">
            <Store className="h-3 w-3 shrink-0" />
            <span className="truncate" data-testid={`search-product-vendor-${p.product_key}`}>Sold by <span className="font-medium text-foreground">{vendor.business_name || "—"}</span></span>
            {vendor.is_verified && <BadgeCheck className="h-3 w-3 text-emerald-600 shrink-0" />}
          </div>
          <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" /> {vendor.city || "—"}
            {typeof vendor.rating === "number" && (
              <span className="ml-2 inline-flex items-center gap-0.5"><Star className="h-3 w-3 fill-amber-500 text-amber-500" /> {vendor.rating.toFixed(1)}</span>
            )}
          </div>

          {Array.isArray(p.tags) && p.tags.length > 0 && (
            <div className="flex items-center gap-1 flex-wrap mt-0.5" data-testid={`search-product-tags-${p.product_key}`}>
              {p.tags.slice(0, 3).map((tag) => (
                <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-100">
                  #{String(tag).replace(/^#/, "")}
                </span>
              ))}
              {p.tags.length > 3 && (
                <span className="text-[10px] text-muted-foreground">+{p.tags.length - 3}</span>
              )}
            </div>
          )}

          <div className="mt-1 flex items-center justify-between">
            {price ? (
              <div className="font-semibold text-sm inline-flex items-center" data-testid={`search-product-price-${p.product_key}`}>
                <IndianRupee className="h-3.5 w-3.5" />
                {price.replace("₹", "")}
              </div>
            ) : <span className="text-[11px] text-muted-foreground">Contact for price</span>}
            {p.is_veg === true && <Badge variant="outline" className="rounded-full text-[10px] border-emerald-500 text-emerald-700">Veg</Badge>}
          </div>
        </div>
      </Link>

      <div className="px-3 pb-3">
        <Button
          size="sm"
          className="rounded-full h-8 text-xs bg-amber-500 hover:bg-amber-600 text-white w-full"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAdd(p); }}
          data-testid={`search-product-add-${p.product_key}`}
        >
          <ShoppingCart className="h-3.5 w-3.5 mr-1" /> Add to cart
        </Button>
      </div>
    </Card>
  );
}

export default function SearchResults() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const city = params.get("city") || "";
  const category = params.get("category") || "";
  const [draft, setDraft] = useState(q);
  const cart = useCart();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [trending, setTrending] = useState([]);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data || [])).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
    api.get("/products/trending-tags", { params: { limit: 10 } })
      .then((r) => setTrending(r.data?.tags || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setDraft(q);
    setLoading(true);
    const p = { page_size: 60 };
    if (q) p.search = q;
    if (city) p.city = city;
    if (category) p.category = category;
    api.get("/products", { params: p })
      .then((r) => {
        setItems(r.data?.items || []);
        setTotal(r.data?.total || 0);
      })
      .catch(() => toast.error("Search failed"))
      .finally(() => setLoading(false));
  }, [q, city, category]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const submit = (e) => {
    e.preventDefault();
    setParam("q", draft.trim());
  };

  const add = async (p) => {
    try {
      await cart.add(p.vendor.vendor_id, p.name, 1, p.price_range || (p.price_value ? `₹${p.price_value}` : ""));
      toast.success(`Added ${p.name} to cart`);
    } catch {
      toast.error("Could not add to cart");
    }
  };

  return (
    <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-4">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <ShoppingBag className="h-3 w-3" /> Items
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter mt-1">Everything from every trusted vendor</h1>
        <p className="text-sm text-muted-foreground mt-1">Browse all community items or search for something specific — add anything to your enquiry cart.</p>
      </div>

      <form onSubmit={submit} className="max-w-4xl mb-3" data-testid="search-form">
        <div className="flex items-center gap-2 bg-background border border-border/60 rounded-2xl p-2 flex-wrap sm:flex-nowrap">
          <Search className="h-4 w-4 shrink-0 ml-1 text-muted-foreground" />
          <Input
            type="search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Search: modak, tiffin, saree, 2 BHK, atta…"
            className="border-0 shadow-none focus-visible:ring-0 h-9 min-w-[160px] flex-1"
            data-testid="search-input"
          />
          <Select value={city || "all"} onValueChange={(v) => setParam("city", v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-36 shrink-0" data-testid="items-city-select">
              <SelectValue placeholder="All cities" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All cities</SelectItem>
              {cities.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={category || "all"} onValueChange={(v) => setParam("category", v === "all" ? "" : v)}>
            <SelectTrigger className="h-9 w-44 shrink-0" data-testid="items-category-select">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button type="submit" className="rounded-xl h-9 px-4 text-sm bg-primary text-white" data-testid="search-submit">
            Search
          </Button>
        </div>
        {(city || category) && (
          <div className="flex items-center gap-2 flex-wrap mt-2" data-testid="items-active-filters">
            {city && (
              <Badge variant="outline" className="rounded-full text-xs gap-1 pl-3 pr-1 py-0.5">
                {city}
                <button type="button" onClick={() => setParam("city", "")} className="ml-1 h-5 w-5 rounded-full hover:bg-muted inline-flex items-center justify-center" data-testid="items-city-clear">×</button>
              </Badge>
            )}
            {category && (
              <Badge variant="outline" className="rounded-full text-xs gap-1 pl-3 pr-1 py-0.5">
                {category}
                <button type="button" onClick={() => setParam("category", "")} className="ml-1 h-5 w-5 rounded-full hover:bg-muted inline-flex items-center justify-center" data-testid="items-category-clear">×</button>
              </Badge>
            )}
          </div>
        )}
      </form>

      {trending.length > 0 && (
        <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="trending-tags-bar">
          <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground shrink-0">
            <TrendingUp className="h-3 w-3" /> Trending
          </div>
          {trending.map((t) => {
            const raw = t.tag || "";
            const bare = raw.replace(/^#+/, "");
            const active = q && q.replace(/^#+/, "").toLowerCase() === bare.toLowerCase();
            return (
              <button
                key={raw}
                type="button"
                onClick={() => {
                  setDraft(raw);
                  setParam("q", active ? "" : raw);
                }}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium border transition-colors ${active
                  ? "bg-primary text-white border-primary"
                  : "bg-primary/5 text-primary border-primary/20 hover:bg-primary/10"}`}
                data-testid={`trending-tag-${bare}`}
                title={`${t.count} item${t.count === 1 ? "" : "s"}`}
              >
                <span>{raw.startsWith("#") ? raw : `#${bare}`}</span>
                <span className="text-[10px] opacity-70">{t.count}</span>
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <Card className="p-10 border-border/60 text-center" data-testid="search-loading">Loading…</Card>
      ) : items.length === 0 ? (
        <Card className="p-10 border-border/60 text-center" data-testid="search-no-results">
          {q
            ? <p className="text-sm text-muted-foreground">No items match &ldquo;{q}&rdquo;. Try a different keyword.</p>
            : <p className="text-sm text-muted-foreground">No items available yet.</p>}
        </Card>
      ) : (
        <>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2 inline-flex items-center gap-1" data-testid="search-count">
            <ShoppingBag className="h-3 w-3" /> {total.toLocaleString("en-IN")} items · showing {items.length}
            {q && <span className="normal-case tracking-normal ml-2">for &ldquo;{q}&rdquo;</span>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="search-product-grid">
            {items.map((p) => <ProductCard key={p.product_key} p={p} onAdd={add} />)}
          </div>
        </>
      )}
    </div>
  );
}
